import { useMemo } from 'react';
import { User } from 'firebase/auth';

// Privacy Types - Centralized definitions
export type PrivacyType = 'private' | 'public' | 'collaborative-private' | 'collaborative-public';

// User Roles in watchlists
export type UserRole = 'owner' | 'admin-collaborator' | 'editor-collaborator' | 'viewer' | 'public-user';

// Watchlist interface for permissions
export interface WatchlistForPermissions {
  id: string;
  createdBy: string;
  createdByName?: string;
  isPrivate: boolean;
  collaborators?: string[];
  /** Firestore field; used when profile entries omit role */
  adminCollaboratorIds?: string[];
  collaboratorProfiles?: Array<{
    uid: string;
    name: string;
    role?: 'admin' | 'editor';
    avatar?: string;
  }>;
  followerCount?: number;
}

// Permission set interface
export interface WatchlistPermissions {
  // User role and status
  userRole: UserRole;
  isOwner: boolean;
  isCollaborator: boolean;
  isAdminCollaborator: boolean;
  isEditorCollaborator: boolean;
  isPublicUser: boolean;
  
  // Privacy information
  privacyType: PrivacyType;
  isPrivate: boolean;
  isPublic: boolean;
  isCollaborative: boolean;
  
  // Core permissions
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canShare: boolean;
  canLike: boolean;
  
  // Management permissions
  canManageCollaborators: boolean;
  canChangePrivacy: boolean;
  canInviteCollaborators: boolean;
  canRemoveCollaborators: boolean;
  canAssignAdminRole: boolean;
  canLeaveCollaboration: boolean;
  
  // Content permissions
  canAddMovies: boolean;
  canRemoveMovies: boolean;
  canEditMovieNotes: boolean;
  canEditListDetails: boolean;
  
  // Visibility permissions
  canSeeCollaborators: boolean;
  canSeeLikes: boolean;
  canSeeInPublicFeeds: boolean;
  canDiscoverInSearch: boolean;
  
  // UI state helpers
  shouldShowLikeButton: boolean;
  shouldShowShareButton: boolean;
  shouldShowEditButton: boolean;
  shouldShowManageButton: boolean;
  shouldShowDeleteButton: boolean;
  shouldShowLeaveButton: boolean;
}

/**
 * Central hook for managing all watchlist permissions and privacy logic
 */
export const useWatchlistPermissions = (
  watchlist: WatchlistForPermissions | null,
  currentUser: User | null,
  isOtherUserWatchlist: boolean = false
): WatchlistPermissions => {
  
  return useMemo(() => {
    // Default permissions for null/undefined states
    if (!watchlist || !currentUser) {
      return {
        userRole: 'public-user',
        isOwner: false,
        isCollaborator: false,
        isAdminCollaborator: false,
        isEditorCollaborator: false,
        isPublicUser: true,
        
        privacyType: 'private',
        isPrivate: true,
        isPublic: false,
        isCollaborative: false,
        
        canView: false,
        canEdit: false,
        canDelete: false,
        canShare: false,
        canLike: false,
        
        canManageCollaborators: false,
        canChangePrivacy: false,
        canInviteCollaborators: false,
        canRemoveCollaborators: false,
        canAssignAdminRole: false,
        canLeaveCollaboration: false,
        
        canAddMovies: false,
        canRemoveMovies: false,
        canEditMovieNotes: false,
        canEditListDetails: false,
        
        canSeeCollaborators: false,
        canSeeLikes: false,
        canSeeInPublicFeeds: false,
        canDiscoverInSearch: false,
        
        shouldShowLikeButton: false,
        shouldShowShareButton: false,
        shouldShowEditButton: false,
        shouldShowManageButton: false,
        shouldShowDeleteButton: false,
        shouldShowLeaveButton: false,
      };
    }

    // Determine user role
    const isOwner = watchlist.createdBy === currentUser.uid || watchlist.createdByName === "You";
    const collaboratorProfile = watchlist.collaboratorProfiles?.find(c => c.uid === currentUser.uid);
    const isCollaborator = watchlist.collaborators?.includes(currentUser.uid) || !!collaboratorProfile;
    const isListedAdmin = Boolean(watchlist.adminCollaboratorIds?.includes(currentUser.uid));
    const isAdminCollaborator =
      isCollaborator && (collaboratorProfile?.role === 'admin' || isListedAdmin);
    const isEditorCollaborator = isCollaborator && !isAdminCollaborator;
    const isPublicUser = !isOwner && !isCollaborator;

    let userRole: UserRole;
    if (isOwner) {
      userRole = 'owner';
    } else if (isAdminCollaborator) {
      userRole = 'admin-collaborator';
    } else if (isEditorCollaborator) {
      userRole = 'editor-collaborator';
    } else if (!watchlist.isPrivate) {
      userRole = 'public-user';
    } else {
      userRole = 'viewer';
    }

    // Determine privacy type
    const hasCollaborators = (watchlist.collaborators?.length || 0) > 0;
    let privacyType: PrivacyType;
    if (watchlist.isPrivate && hasCollaborators) {
      privacyType = 'collaborative-private';
    } else if (!watchlist.isPrivate && hasCollaborators) {
      privacyType = 'collaborative-public';
    } else if (watchlist.isPrivate) {
      privacyType = 'private';
    } else {
      privacyType = 'public';
    }

    const isPrivate = watchlist.isPrivate;
    const isPublic = !watchlist.isPrivate;
    const isCollaborative = hasCollaborators;

    // Core permissions
    const canView = isOwner || isCollaborator || isPublic;
    const canEdit = isOwner || isCollaborator;
    const canDelete = isOwner; // Only owner can delete
    const canShare = true; // Everyone can share (with appropriate privacy constraints)
    const canLike = isPublicUser && isPublic; // Only public users can like public lists

    // Management permissions
    const canManageCollaborators = isOwner || isAdminCollaborator;
    const canChangePrivacy = isOwner; // Only owner can change privacy
    const canInviteCollaborators = isOwner || isAdminCollaborator;
    const canRemoveCollaborators = isOwner || isAdminCollaborator;
    const canAssignAdminRole = isOwner;
    const canLeaveCollaboration = isCollaborator && !isOwner;

    // Content permissions
    const canAddMovies = canEdit;
    const canRemoveMovies = canEdit;
    const canEditMovieNotes = canEdit;
    const canEditListDetails = canEdit;

    // Visibility permissions
    const canSeeCollaborators = canView;
    const canSeeLikes = isPublic || isOwner;
    const canSeeInPublicFeeds = isPublic;
    const canDiscoverInSearch = isPublic;

    // UI state helpers
    const shouldShowLikeButton = canLike;
    const shouldShowShareButton = canShare;
    const shouldShowEditButton = canEdit && !isOtherUserWatchlist;
    const shouldShowManageButton = canManageCollaborators && !isOtherUserWatchlist;
    const shouldShowDeleteButton = canDelete && !isOtherUserWatchlist;
    const shouldShowLeaveButton = canLeaveCollaboration;

    return {
      userRole,
      isOwner,
      isCollaborator,
      isAdminCollaborator,
      isEditorCollaborator,
      isPublicUser,
      
      privacyType,
      isPrivate,
      isPublic,
      isCollaborative,
      
      canView,
      canEdit,
      canDelete,
      canShare,
      canLike,
      
      canManageCollaborators,
      canChangePrivacy,
      canInviteCollaborators,
      canRemoveCollaborators,
      canAssignAdminRole,
      canLeaveCollaboration,
      
      canAddMovies,
      canRemoveMovies,
      canEditMovieNotes,
      canEditListDetails,
      
      canSeeCollaborators,
      canSeeLikes,
      canSeeInPublicFeeds,
      canDiscoverInSearch,
      
      shouldShowLikeButton,
      shouldShowShareButton,
      shouldShowEditButton,
      shouldShowManageButton,
      shouldShowDeleteButton,
      shouldShowLeaveButton,
    };
  }, [watchlist, currentUser, isOtherUserWatchlist]);
};

/**
 * Helper function to get privacy type display information
 */
export const getPrivacyTypeInfo = (privacyType: PrivacyType, collaboratorCount: number = 0, followerCount: number = 0) => {
  switch (privacyType) {
    case 'private':
      return {
        icon: 'Lock',
        label: 'Private',
        description: 'Only you can see this list',
        color: 'text.secondary'
      };
    case 'public':
      return {
        icon: 'Public',
        label: 'Public',
        description: `${followerCount === 1 ? '1 like' : `${followerCount || 0} likes`}`,
        color: 'success.main'
      };
    case 'collaborative-private':
      return {
        icon: 'Group',
        label: 'Shared',
        description: `Shared with ${collaboratorCount} other${collaboratorCount === 1 ? '' : 's'}`,
        color: 'warning.main'
      };
    case 'collaborative-public':
      return {
        icon: 'Group',
        label: 'Collaborative',
        description: `${followerCount === 1 ? '1 like' : `${followerCount || 0} likes`} • ${collaboratorCount} collaborator${collaboratorCount === 1 ? '' : 's'}`,
        color: 'primary.main'
      };
    default:
      return {
        icon: 'Public',
        label: 'Public',
        description: 'Public list',
        color: 'text.secondary'
      };
  }
};

/**
 * Helper function to determine if privacy can be changed
 */
export const canChangePrivacyTo = (
  currentPrivacyType: PrivacyType,
  targetPrivacyType: PrivacyType,
  permissions: WatchlistPermissions
): boolean => {
  if (!permissions.canChangePrivacy) return false;

  // Owner can always change between private/public if no collaborators
  if (!permissions.isCollaborative) {
    return targetPrivacyType === 'private' || targetPrivacyType === 'public';
  }

  // With collaborators, can toggle between collaborative-private and collaborative-public
  if (permissions.isCollaborative) {
    return targetPrivacyType === 'collaborative-private' || targetPrivacyType === 'collaborative-public';
  }

  return false;
};

export default useWatchlistPermissions; 